import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const HypotheticalDisclaimerPage = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 py-20">
      <SEO title="Hypothetical Performance Disclaimer" description="CFTC and NFA required hypothetical performance disclaimer for NinjaCopia." />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="prose prose-lg dark:prose-invert max-w-none"
        >
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8">
            Hypothetical Performance Disclaimer
          </h1>

          <div className="text-sm text-gray-500 dark:text-gray-400 mb-8">
            CFTC Rule 4.41 / NFA Compliance Rule 2-29
          </div>

          <div className="space-y-8">
            <section>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
                <p className="text-yellow-800 dark:text-yellow-200 font-bold text-sm leading-relaxed mb-4">
                  HYPOTHETICAL PERFORMANCE RESULTS HAVE MANY INHERENT LIMITATIONS, SOME OF WHICH ARE DESCRIBED BELOW. NO REPRESENTATION IS BEING MADE THAT ANY ACCOUNT WILL OR IS LIKELY TO ACHIEVE PROFITS OR LOSSES SIMILAR TO THOSE SHOWN. IN FACT, THERE ARE FREQUENTLY SHARP DIFFERENCES BETWEEN HYPOTHETICAL PERFORMANCE RESULTS AND THE ACTUAL RESULTS SUBSEQUENTLY ACHIEVED BY ANY PARTICULAR TRADING PROGRAM.
                </p>
                <p className="text-yellow-800 dark:text-yellow-200 font-bold text-sm leading-relaxed mb-4">
                  ONE OF THE LIMITATIONS OF HYPOTHETICAL PERFORMANCE RESULTS IS THAT THEY ARE GENERALLY PREPARED WITH THE BENEFIT OF HINDSIGHT. IN ADDITION, HYPOTHETICAL TRADING DOES NOT INVOLVE FINANCIAL RISK, AND NO HYPOTHETICAL TRADING RECORD CAN COMPLETELY ACCOUNT FOR THE IMPACT OF FINANCIAL RISK IN ACTUAL TRADING.
                </p>
                <p className="text-yellow-800 dark:text-yellow-200 font-bold text-sm leading-relaxed mb-4">
                  FOR EXAMPLE, THE ABILITY TO WITHSTAND LOSSES OR TO ADHERE TO A PARTICULAR TRADING PROGRAM IN SPITE OF TRADING LOSSES ARE MATERIAL POINTS WHICH CAN ALSO ADVERSELY AFFECT ACTUAL TRADING RESULTS.
                </p>
                <p className="text-yellow-800 dark:text-yellow-200 font-bold text-sm leading-relaxed">
                  THERE ARE NUMEROUS OTHER FACTORS RELATED TO THE MARKETS IN GENERAL OR TO THE IMPLEMENTATION OF ANY SPECIFIC TRADING PROGRAM WHICH CANNOT BE FULLY ACCOUNTED FOR IN THE PREPARATION OF HYPOTHETICAL PERFORMANCE RESULTS AND ALL OF WHICH CAN ADVERSELY AFFECT ACTUAL TRADING RESULTS.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Understanding This Disclaimer</h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                This disclaimer is required by the Commodity Futures Trading Commission (CFTC) and the National Futures Association (NFA) whenever hypothetical or simulated performance results are presented. The key points to understand are:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-3 ml-4">
                <li><strong>Hindsight Bias:</strong> Hypothetical results are often created after the fact, making it easy to show profitable trades that may not have been identifiable in real-time.</li>
                <li><strong>No Financial Risk:</strong> Simulated trading doesn't involve real money, so the emotional and financial pressures of actual trading are absent.</li>
                <li><strong>Execution Differences:</strong> Real trading involves slippage, commissions, and execution delays that hypothetical results may not account for.</li>
                <li><strong>Market Conditions:</strong> Past market conditions may not repeat, and strategies that worked historically may not work in the future.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Application to NinjaCopia</h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                NinjaCopia is trade copying software. We do not:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-2 ml-4">
                <li>Provide trading signals or recommendations</li>
                <li>Publish hypothetical performance results</li>
                <li>Guarantee any trading outcomes</li>
                <li>Offer investment advice</li>
              </ul>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mt-4">
                This disclaimer is provided for regulatory compliance and to ensure you understand the limitations of any performance data you may encounter in the trading industry.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Additional Resources</h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                For more information about these regulatory requirements, please visit:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-2 ml-4 mt-4">
                <li>
                  <a
                    href="https://www.nfa.futures.org/rulebooksql/rules.aspx?Section=4&RuleID=RULE+2-29"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    NFA Compliance Rule 2-29
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.cftc.gov/LawRegulation/CommodityExchangeAct/index.htm"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    CFTC Regulations
                  </a>
                </li>
              </ul>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default HypotheticalDisclaimerPage;
